import { useState, useEffect, useRef } from 'react';
import { GitBranch, CheckCircle, XCircle,
         Loader, ExternalLink } from 'lucide-react';
import toast from 'react-hot-toast';
import axios from 'axios';

interface Props {
  request: any;
  token: string;
  onClose: () => void;
}

const GitHubPushModal = ({ request, token, onClose }: Props) => {
  const [form, setForm] = useState({
    owner:  '',
    repo:   '',
    branch: 'main',
    pat:    ''
  });

  const [loading, setLoading]           = useState(false);
  const [result, setResult]             = useState<any>(null);
  const [buildStatus, setBuildStatus]   = useState<any>(null);
  const [polling, setPolling]           = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const API_URL =
    process.env.REACT_APP_API_URL || 'http://localhost:5062';

  // Cleanup on unmount ✅
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, []);

  // ── Push to GitHub ────────────────────────────
  const handlePush = async () => {
    if (!form.owner.trim() ||
        !form.repo.trim()  ||
        !form.pat.trim()) {
      toast.error('Please fill all fields!');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(
        `${API_URL}/api/GitHub/push`,
        {
          ...request,
          gitHubOwner:  form.owner,
          gitHubRepo:   form.repo,
          gitHubBranch: form.branch,
          gitHubToken:  form.pat
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setResult(response.data);
      toast.success('Pushed to GitHub! 🚀');

      if (request.ciCdTool !== 0) {
        setTimeout(() => {
          startPolling(form.owner, form.repo, form.pat);
        }, 8000);
      }

    } catch (err: any) {
      toast.error(
        err.response?.data?.error || 'Push failed!');
    } finally {
      setLoading(false);
    }
  };

  // ── Poll Build Status ─────────────────────────
  const startPolling = (
    owner: string,
    repo: string,
    pat: string
  ) => {
    setPolling(true);
    setPollAttempts(0);

    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    let attempts = 0;

    intervalRef.current = setInterval(async () => {
      attempts++;
      setPollAttempts(attempts);

      if (attempts > 24) {
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
        setPolling(false);
        setBuildStatus((prev: any) => ({
          ...prev,
          status:  'timeout',
          message: '⏱️ Timed out — check GitHub Actions directly'
        }));
        return;
      }

      try {
        const response = await axios.get(
          `${API_URL}/api/github/status`,
          {
            params:  { owner, repo, token: pat },
            headers: { Authorization: `Bearer ${token}` }
          }
        );

        const data = response.data;
        setBuildStatus(data);

        const isCompleted =
          data.status     === 'completed'  ||
          data.conclusion === 'success'    ||
          data.conclusion === 'failure'    ||
          data.conclusion === 'cancelled'  ||
          data.conclusion === 'skipped';

        if (isCompleted) {
          clearInterval(intervalRef.current!);
          intervalRef.current = null;
          setPolling(false);

          if (data.conclusion === 'success') {
            toast.success('Build passed! ✅');
          } else if (data.conclusion === 'failure') {
            toast.error('Build failed! Check GitHub Actions.');
          }
        }

      } catch { /* keep polling */ }

    }, 5000);
  };

  // ── Status display helper ─────────────────────
  const getStatusDisplay = () => {
    if (!buildStatus) return null;

    const { status, conclusion, message } = buildStatus;

    if (status === 'timeout') return {
      color: 'bg-yellow-500/10 border-yellow-500/20 text-yellow-400',
      icon:  <ExternalLink className="w-4 h-4 inline mr-2" />,
      text:  message
    };

    if (conclusion === 'success') return {
      color: 'bg-green-500/10 border-green-500/20 text-green-400',
      icon:  <CheckCircle className="w-4 h-4 inline mr-2" />,
      text:  '✅ Build passed! Docker image pushed to ghcr.io'
    };

    if (conclusion === 'failure') return {
      color: 'bg-red-500/10 border-red-500/20 text-red-400',
      icon:  <XCircle className="w-4 h-4 inline mr-2" />,
      text:  '❌ Build failed — check GitHub Actions for details'
    };

    if (status === 'in_progress' ||
        status === 'queued') return {
      color: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
      icon:  <Loader className="w-4 h-4 animate-spin inline mr-2" />,
      text:  message || '🔄 Build running...'
    };

    return {
      color: 'bg-blue-500/10 border-blue-500/20 text-blue-400',
      icon:  <Loader className="w-4 h-4 animate-spin inline mr-2" />,
      text:  message || '⏳ Waiting for build...'
    };
  };

  const statusDisplay = getStatusDisplay();

  const deployPlatforms = [
    { label: 'AWS',        url: 'https://aws.amazon.com/ecs' },
    { label: 'Azure',      url: 'https://azure.microsoft.com/en-us/products/container-apps' },
    { label: 'GCP',        url: 'https://cloud.google.com/run' },
    { label: 'Railway',    url: 'https://railway.app' },
    { label: 'Kubernetes', url: 'https://kubernetes.io' },
  ];

  // ── Render ────────────────────────────────────
  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50
                    flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-2xl
                      p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">

        {/* Header */}
        <div className="flex items-center gap-2 mb-6">
          <GitBranch className="w-5 h-5 text-gray-300" />
          <h3 className="text-blue-200 font-semibold text-lg">
            Push to GitHub
          </h3>
        </div>

        {!result ? (
          <>
            {/* Warning */}
            <div className="bg-yellow-500/10 border border-yellow-500/20
                            rounded-xl p-3 mb-4">
              <p className="text-yellow-400 text-xs font-semibold mb-1">
                ⚠️ Before pushing:
              </p>
              <ul className="text-gray-500 text-xs space-y-1">
                <li>→ Create an empty repo on GitHub</li>
                <li>→ Generate PAT with repo + workflow scopes</li>
                <li>→ Keep repo empty (no README)</li>
              </ul>
              <a href="https://github.com/new"
                 target="_blank"
                 rel="noreferrer"
                 className="text-blue-400 text-xs mt-2
                            inline-block hover:text-blue-300">
                Create repo on GitHub →
              </a>
            </div>

            {/* Form */}
            <div className="space-y-4 mb-6">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-1">
                    GitHub Username *
                  </label>
                  <input
                    value={form.owner}
                    onChange={e =>
                      setForm({ ...form, owner: e.target.value })}
                    placeholder="hbaswapu"
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-3 py-2.5 text-sm
                               outline-none focus:border-blue-500
                               transition-colors"
                  />
                </div>
                <div>
                  <label className="block text-gray-400
                                    text-xs font-mono mb-1">
                    Repository *
                  </label>
                  <input
                    value={form.repo}
                    onChange={e =>
                      setForm({ ...form, repo: e.target.value })}
                    placeholder="my-api"
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl px-3 py-2.5 text-sm
                               outline-none focus:border-blue-500
                               transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400
                                  text-xs font-mono mb-1">
                  Branch
                </label>
                <div className="relative">
                  <GitBranch className="absolute left-3 top-1/2
                                        -translate-y-1/2 w-4 h-4
                                        text-gray-500" />
                  <input
                    value={form.branch}
                    onChange={e =>
                      setForm({ ...form, branch: e.target.value })}
                    placeholder="main"
                    className="w-full bg-gray-800 border border-gray-700
                               text-gray-200 rounded-xl pl-9 pr-3
                               py-2.5 text-sm outline-none
                               focus:border-blue-500 transition-colors"
                  />
                </div>
              </div>

              <div>
                <label className="block text-gray-400
                                  text-xs font-mono mb-1">
                  Personal Access Token *
                </label>
                <input
                  type="password"
                  value={form.pat}
                  onChange={e =>
                    setForm({ ...form, pat: e.target.value })}
                  placeholder="ghp_xxxxxxxxxxxx"
                  className="w-full bg-gray-800 border border-gray-700
                             text-gray-200 rounded-xl px-3 py-2.5 text-sm
                             outline-none focus:border-blue-500
                             transition-colors"
                />
                <p className="text-gray-600 text-xs mt-1">
                  Needs: repo, workflow permissions
                </p>
              </div>
            </div>

            <div className="flex gap-3">
              <button
                onClick={onClose}
                className="flex-1 py-3 rounded-xl bg-gray-800
                           hover:bg-gray-700 text-gray-400
                           border border-gray-700
                           transition-colors text-sm">
                Cancel
              </button>
              <button
                onClick={handlePush}
                disabled={loading}
                className="flex-1 py-3 rounded-xl bg-blue-600
                           hover:bg-blue-500 text-white font-semibold
                           transition-colors text-sm flex items-center
                           justify-center gap-2 disabled:opacity-50">
                {loading ? (
                  <>
                    <Loader className="w-4 h-4 animate-spin" />
                    Pushing...
                  </>
                ) : (
                  <>
                    <GitBranch className="w-4 h-4" />
                    Push to GitHub
                  </>
                )}
              </button>
            </div>
          </>
        ) : (
          // ── Success View ──────────────────────
          <div className="text-center">
            <CheckCircle className="w-12 h-12 text-green-400
                                    mx-auto mb-4" />
            <h4 className="text-white font-semibold text-lg mb-2">
              Pushed Successfully! 🚀
            </h4>
            <p className="text-gray-400 text-sm mb-4">
              {result.pushedFiles} files pushed to {result.branch}
            </p>

            {/* Build Status section ✅ */}
            {request.ciCdTool !== 0 && (
              <>
                {/* Waiting to start */}
                {!buildStatus && polling && (
                  <div className="bg-blue-500/10 border border-blue-500/20
                                  rounded-xl p-4 mb-4 text-blue-400
                                  text-sm font-mono">
                    <Loader className="w-4 h-4 animate-spin inline mr-2" />
                    Waiting for build to start...
                    <p className="text-gray-600 text-xs mt-1">
                      (checking in {5 - (pollAttempts % 5)}s)
                    </p>
                  </div>
                )}

                {/* Build status display */}
                {buildStatus && statusDisplay && (
                  <div className={`rounded-xl p-4 mb-4 border
                                   text-sm font-mono text-left
                                   ${statusDisplay.color}`}>
                    <div className="flex items-center gap-2">
                      {polling
                        ? <Loader className="w-4 h-4 animate-spin shrink-0" />
                        : statusDisplay.icon
                      }
                      <span>{statusDisplay.text}</span>
                    </div>
                    {polling && (
                      <p className="text-xs mt-2 opacity-60">
                        Checking build status... ({pollAttempts}/24)
                      </p>
                    )}
                  </div>
                )}
              </>
            )}

            {/* Docker Image Ready ✅ */}
            {buildStatus?.conclusion === 'success' && (
              <div className="bg-gray-800 border border-green-500/20
                              rounded-xl p-4 mb-4 text-left">
                <p className="text-green-400 text-xs
                              font-semibold mb-3">
                  🐳 Docker Image Ready!
                </p>

                {/* Pull command */}
                <div className="bg-gray-900 rounded-lg p-3 mb-3
                                flex items-center justify-between gap-2">
                  <code className="text-blue-400 text-xs
                                   font-mono break-all">
                    docker pull ghcr.io/{form.owner}/{form.repo}:latest
                  </code>
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(
                        `docker pull ghcr.io/${form.owner}/${form.repo}:latest`
                      );
                      toast.success('Copied! 📋');
                    }}
                    className="text-gray-500 hover:text-gray-300
                               text-xs shrink-0 transition-colors">
                    Copy
                  </button>
                </div>

                {/* Deploy platforms */}
                <p className="text-gray-500 text-xs mb-2">
                  Deploy to:
                </p>
                <div className="flex flex-wrap gap-2">
                  {deployPlatforms.map(platform => (
                    <a
                      key={platform.label}
                      href={platform.url}
                      target="_blank"
                      rel="noreferrer"
                      className="px-3 py-1.5 rounded-lg bg-gray-700
                                 hover:bg-gray-600 text-gray-300 text-xs
                                 font-medium transition-colors
                                 border border-gray-600">
                      {platform.label}
                    </a>
                  ))}
                </div>
              </div>
            )}

            {/* No CI/CD message ✅ */}
            {request.ciCdTool === 0 && (
              <div className="bg-gray-800 border border-blue-500/20
                              rounded-xl p-4 mb-4 text-left">
                <p className="text-blue-400 text-xs font-semibold mb-1">
                  🐳 Your code is on GitHub!
                </p>
                <p className="text-gray-500 text-xs">
                  Add GitHub Actions CI/CD on next generate
                  to auto-build Docker image.
                </p>
              </div>
            )}

            {/* Links */}
            <div className="flex gap-3 mb-3">
              <a
                href={result.repoUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 py-3 rounded-xl bg-gray-800
                           hover:bg-gray-700 text-gray-300
                           border border-gray-700 transition-colors
                           text-sm text-center">
                View Repo
              </a>
              {result.actionsUrl && (
                <a
                  href={result.actionsUrl}
                  target="_blank"
                  rel="noreferrer"
                  className="flex-1 py-3 rounded-xl bg-green-700
                             hover:bg-green-600 text-white font-semibold
                             transition-colors text-sm text-center">
                  {polling ? '🔄 View Build' : '✅ View Build'}
                </a>
              )}
            </div>

            <button
              onClick={onClose}
              className="w-full py-2.5 rounded-xl bg-gray-800
                         hover:bg-gray-700 text-gray-400 text-sm
                         transition-colors">
              Close
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GitHubPushModal;